import { trackGraphicPlacementPinChanged } from '@src/analytics/EventTracker';
import Switch from '@src/components/common/Switch';
import { PARAGRAPH_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { CompositionNormalizer } from '@src/components/sidepanel/compositionNormalizer/compositionNormalizer';
import { GenericAnimationSettings } from '@src/components/sidepanel/GenericAnimationSettings';
import { useMatchCardToVideoDurationService } from '@src/hooks/useMatchCardToVideoDurationService';
import { mapToElementState } from '@src/renderer/ElementState';
import { standardizeControlLabels } from '@src/services/composition/templateControlNormalization';
import {
  isUploadedMediaCard,
  isUploadedMediaCardId,
} from '@src/services/videoTemplate/uploadedMediaCard';
import {
  useStore,
  useUserIdentity,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import { SidebarOption } from '@src/types.ts/general';
import {
  applySpeakerControlValueMappingToControls,
  calculateUpdatedControlValues,
  modifyCompositionProperty,
  normalizeSpeakerForControlBindings,
  upsertUpdatedValue,
} from '@src/utility/compositionSettings';
import {
  getElementMetaObject,
  isEndCardMeta,
  readPlacementMeta,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { buildOverlayMarker } from '@src/utility/overlayMarker';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { Breadcrumbs } from '../../Breadcrumbs';
import { useCompositionContext } from '../CompositionContext';
import { SettingsPreviewSection } from '../SettingsPreviewSection';
import { CompositionControl } from './CompositionControl';
import { CompositionSaveTemplate } from './CompositionSaveTemplate';
import { useCreatorStudioAITextGeneration } from './controls/useCreatorStudioAITextGeneration';
import { templateGroupLabel } from './templateGroup';

import type { AITextPanelContext } from './controls/AITextControlPanelAdapter';
import type {
  AspectConfigKey,
  AspectConfiguration,
  Category,
  CompositionTemplate,
  ControlConfiguration,
  Subcategory,
} from './types';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { ElementState } from '@src/renderer/ElementState';
import type { OverlayMarkerAnchorInput } from '@src/utility/overlayMarker';

const logger = LogManager.getLogger('CompositionControls');

interface CompositionControlsProps {
  activeElement: ElementState;
}

// Aspect ratio → the CompositionTemplate field holding that ratio's placement.
const ASPECT_CONFIG_KEY: Record<string, AspectConfigKey> = {
  '16:9': 'sixteenByNine',
  '9:16': 'nineBySixteen',
  '1:1': 'oneByOne',
};

export const CompositionControls: React.FC<CompositionControlsProps> = observer(
  ({ activeElement }) => {
    const videoCreator = useVideoCreatorStore();
    const { editorStore } = useStore();
    const videoSourceStore = useVideoSource();
    const matchCardToVideoDurationService =
      useMatchCardToVideoDurationService();

    // Re-derive the selected element from the live (observable) video source so
    // edits made OUTSIDE this panel — the canvas / transcript quick-edit popover,
    // which writes via `commandUpdateElement` → `setSource` — are reflected in
    // the control inputs. The `activeElement` prop is a snapshot captured at
    // selection time and is never updated by those external mutations, so without
    // this the inputs showed stale values until the element was reselected.
    // Reading `videoSourceStore.source` (an `observable.ref` replaced wholesale
    // on every sync) inside the `observer` re-renders this panel on each edit;
    // `getUnifiedValue` then reads the fresh leaf value off the remapped element.
    const liveActiveElement = useMemo<ElementState>(() => {
      const id = activeElement.source.id;
      const fresh =
        typeof id === 'string'
          ? videoSourceStore.findElementById(id)
          : undefined;
      return fresh ? mapToElementState(fresh) : activeElement;
    }, [videoSourceStore.source, activeElement]);
    const {
      getCompositionWithCategoryAndSubcategoryById,
      setSelectedCategoryId,
      setSelectedSubcategoryId,
    } = useCompositionContext();
    const userIdentity = useUserIdentity();
    const { enableCompositionSaveUpdateTemplate } = useFlagsCombination(
      userIdentity.currentRole,
    );
    const recalculationTrigger = useRef<boolean>(false);
    const pinToEndTooltipId = useId();
    const [updatedValues, setUpdatedValues] = useState<
      {
        elementPaths: number[][];
        value: unknown;
        property: string;
      }[]
    >([]);

    const meta = useMemo(
      () =>
        getElementMetaObject(
          videoCreator.currentVideo,
          activeElement.source.id,
        ),
      [videoCreator.currentVideo, activeElement.source.id],
    );

    const templateId = useMemo(() => {
      if (meta && typeof meta === 'object') {
        for (const [k, v] of Object.entries(meta)) {
          if (k === 'templateId' && typeof v === 'string') {
            return v;
          }
        }
      }
      return undefined;
    }, [meta]);

    // TODO: now that we are persisting the controls in extraElementData, we
    // should read from there. We no longer need to access the composition directly.

    const { composition, category, subcategory } = useMemo(() => {
      let composition: CompositionTemplate | undefined;
      let category: Category | undefined;
      let subcategory: Subcategory | undefined;
      if (templateId) {
        const result = getCompositionWithCategoryAndSubcategoryById(templateId);
        if (result.composition) {
          composition = result.composition;
          category = result.category;
          subcategory = result.subcategory;
        } else if (meta) {
          composition = {
            ...(meta as unknown as CompositionTemplate),
            id: meta.templateId as string,
            controls: meta.compositionControls as ControlConfiguration[],
          };
        }
      }
      // Renumber generic Creatomate placeholder text labels ("Text-TLB" → "Text-1")
      // for display. Cosmetic only (matching is by property + elementPaths); flows
      // to both the control rows and the Update/Apply Standards modals below.
      if (composition) {
        composition = {
          ...composition,
          controls: standardizeControlLabels(
            composition.source,
            composition.controls,
          ),
        };
      }
      return { composition, category, subcategory };
    }, [templateId, meta, getCompositionWithCategoryAndSubcategoryById]);

    /*
       PLAT-10920 — the generation wiring for this panel's `aiText` controls,
       built once per selected graphic rather than once per control: the marker
       and the transcript are properties of the graphic. Same hook and same
       service the two quick-edit surfaces use, so a card behaves identically
       wherever it is edited (E1).

       Unconditional: hooks can't be conditional, and the cost when the
       composition has no `aiText` control is a memo and an empty status map.
    */
    const elementId = String(activeElement.source.id ?? '');
    const { generation: aiTextGeneration, transcriptElements } =
      useCreatorStudioAITextGeneration('creator-studio-settings-panel');
    const aiTextOverlay = useMemo(() => {
      // The panel has no marker list, so synthesize the shape
      // `findOverlayRanges` maps from — the placement's stored
      // `transcriptAnchor` when it has one, and the element's time window
      // otherwise. A card added from the design grid takes the latter path and
      // still answers "selection" (DS3).
      //
      // Depends on `videoSourceStore.source` (the observable ref replaced
      // wholesale on every sync) for the same reason `liveActiveElement` above
      // does: dragging the graphic on the timeline while its settings panel is
      // open changes the window Selection resolves through, and a marker
      // memoized only on the element id would keep previewing the old words.
      const timelineEl = editorStore.editor.timeline.getElement(elementId);
      const meta = videoCreator.currentVideo?.extraElementData?.[elementId] as
        | {
            transcriptAnchor?: OverlayMarkerAnchorInput;
            placement?: string | null;
          }
        | undefined;
      return buildOverlayMarker({
        id: elementId,
        time: timelineEl?.time,
        endTime: timelineEl?.end_time,
        anchor: meta?.transcriptAnchor,
        // DS14 — a clip-boundary graphic covers more than its anchored passage,
        // so Selection must resolve to the same range the underline draws.
        placement: meta?.placement,
      });
    }, [
      elementId,
      editorStore,
      videoCreator.currentVideo,
      videoSourceStore.source,
    ]);
    const aiTextContext = useMemo<AITextPanelContext>(
      () => ({
        generation: aiTextGeneration,
        transcriptElements,
        overlay: aiTextOverlay,
      }),
      [aiTextGeneration, transcriptElements, aiTextOverlay],
    );

    const modifyProperty = (control: ControlConfiguration, value: unknown) => {
      if (!control.elementPaths || !control?.property) return;
      const elementPaths = control.elementPaths;
      const property = control?.property;
      const cardElementId = activeElement?.source.id || '';

      modifyCompositionProperty(
        cardElementId,
        editorStore,
        elementPaths,
        property,
        value,
      );
      recalculationTrigger.current = !recalculationTrigger.current;

      if (
        control.type === 'video' &&
        control.matchCardDuration &&
        typeof value === 'string' &&
        value
      ) {
        void matchCardToVideoDurationService
          .matchCardToVideo(cardElementId, value)
          .then(result => {
            if (result.ok) videoSourceStore.refreshSource();
          });
      }

      // Record the edit directly so the Save Changes button reflects ANY control
      // change (font, color, text, …), not only those the value-diff recompute
      // catches, and so the save persists it. Keyed by property + elementPaths.
      setUpdatedValues(prev =>
        upsertUpdatedValue(prev, { elementPaths, value, property }),
      );
    };

    useEffect(() => {
      recalculateUpdatedValues();
    }, [
      composition?.controls,
      liveActiveElement,
      videoCreator.currentVideo,
      recalculationTrigger.current,
    ]);

    const handleSpeakerControlChange = (
      inputMapping: Record<string, string>,
      speaker: Speaker,
    ) => {
      const plain: Record<string, unknown> = {};
      Object.entries(speaker).forEach(([k, v]) => {
        plain[k] = v;
      });
      normalizeSpeakerForControlBindings(plain);

      applySpeakerControlValueMappingToControls(
        activeElement?.source.id || '',
        editorStore,
        composition?.controls ?? [],
        inputMapping,
        plain,
      );

      upsertElementMetaObject(
        videoCreator.currentVideo,
        activeElement.source.id,
        { speakerLabel: plain.speakerLabel },
      );

      recalculationTrigger.current = true;
    };

    const recalculateUpdatedValues = () => {
      const computed = calculateUpdatedControlValues({
        controls: composition?.controls,
        activeElement: liveActiveElement,
        extraElementData: videoCreator.currentVideo?.extraElementData,
        organization: videoCreator.organization,
        story: videoCreator.story,
      });
      // Direct edits (modifyProperty: color/font/text) are the user's fresh,
      // authoritative values. The diff recompute reads getUnifiedValue, which
      // lags the debounced renderer sync and can be stale — so overlay the direct
      // edits ON TOP of the computed diffs (computed only fills in controls the
      // user hasn't directly edited, e.g. stage/speaker changes). The reverse
      // order let a stale recompute clobber a just-committed color.
      setUpdatedValues(prev =>
        prev.reduce((acc, entry) => upsertUpdatedValue(acc, entry), computed),
      );
    };

    // --- Position/size (placement) change tracking ---------------------------
    // Repositioning/resizing happens on the stage (not via modifyProperty), so we
    // watch the active element's live placement to (a) enable Save Changes when it
    // moves and (b) persist the new placement into the current aspect ratio config.
    const livePlacementSignature = (() => {
      const id = activeElement?.source?.id;
      if (!id) return '';
      const el = editorStore.editor.getElementById(id) as unknown as
        | { x?: unknown; y?: unknown; width?: unknown; height?: unknown }
        | undefined;
      if (!el) return '';
      return [el.x, el.y, el.width, el.height]
        .map(v => (v == null ? '' : String(v)))
        .join('|');
    })();

    const basePlacementRef = useRef<string | null>(null);
    const [positionDirty, setPositionDirty] = useState(false);

    useEffect(() => {
      if (!livePlacementSignature) return;
      if (basePlacementRef.current === null) {
        basePlacementRef.current = livePlacementSignature; // baseline at load
        return;
      }
      if (livePlacementSignature !== basePlacementRef.current) {
        setPositionDirty(true);
      }
    }, [livePlacementSignature]);

    // Snapshot the current on-screen placement into the active aspect ratio's
    // config (positionX/positionY + per-axis scale), captured at save time.
    const getCurrentAspectPlacement = ():
      | { key: AspectConfigKey; config: AspectConfiguration }
      | undefined => {
      const id = activeElement?.source?.id;
      if (!id) return undefined;
      const el = editorStore.editor.getElementById(id) as unknown as
        | { x?: unknown; y?: unknown; width?: unknown; height?: unknown }
        | undefined;
      if (!el) return undefined;
      // Position is a Creatomate relative unit (a percentage string like "60%").
      // After a stage drag the renderer can echo x/y back as a bare NUMBER instead
      // of a "%" string — the Element type says `string`, but that isn't enforced
      // at runtime. The old `typeof === 'string'` guard silently dropped those, so
      // moving the composition enabled Save Changes (the dirty signature uses
      // String()) but never persisted the new position. Coerce: keep a non-empty
      // string as-is; treat a finite number as a percentage magnitude.
      const toPosition = (v: unknown): string | undefined => {
        if (typeof v === 'string' && v.trim() !== '') return v.trim();
        if (typeof v === 'number' && Number.isFinite(v)) return `${v}%`;
        return undefined;
      };
      const positionX = toPosition(el.x);
      const positionY = toPosition(el.y);
      const key =
        ASPECT_CONFIG_KEY[String(videoCreator.getCurrentAspectRatio())];
      if (!positionX || !positionY || !key) {
        // Never fail silently — surface why a move didn't persist.
        logger.warn('Skipping composition placement capture', {
          id,
          x: el.x,
          y: el.y,
          xType: typeof el.x,
          aspectRatio: String(videoCreator.getCurrentAspectRatio()),
          key,
        });
        return undefined;
      }
      // Size → per-axis scale, derived absolutely from the composition's authored
      // source dimensions (the exact inverse of how the rendered size was
      // computed). Two axes because the canvas permits non-uniform resizes: a
      // single scale can only encode a uniform one and silently dropped the
      // height component of everything else.
      //
      // Deliberately reads no previously stored scale and no load-time baseline.
      // The old `oldScale × (currentWidth / widthAtLoad)` form was equivalent
      // only while the baseline stayed in sync with the stored scale, which an
      // aspect-ratio switch or a create/save_as breaks.
      const scales = composition
        ? CompositionNormalizer.computeAxisScalesFromLiveSize(
            composition,
            videoCreator.getCurrentAspectRatio(),
            el.width,
            el.height,
          )
        : undefined;
      if (!scales) {
        // Position still persists; scale keeps whatever the template stores.
        // Fullscreen compositions have no meaningful scale, so they're expected
        // here and not worth a warning.
        if (!composition?.isFullscreen) {
          logger.warn('Skipping composition scale capture', {
            id,
            width: el.width,
            height: el.height,
            sourceWidth: composition?.source?.width,
            sourceHeight: composition?.source?.height,
          });
        }
        return { key, config: { positionX, positionY } };
      }
      return {
        key,
        config: {
          positionX,
          positionY,
          // Legacy `scale` is dual-written (= scaleX) so a client still running
          // the previous bundle renders uniformly rather than falling back to 1.
          // Remove one release after this ships.
          scale: scales.scaleX,
          scaleX: scales.scaleX,
          scaleY: scales.scaleY,
        },
      };
    };

    // End cards pinned to the end (placement: 'end') auto-follow the content end
    // via `PlacementReconciler`. Read the live pin state from the observable
    // source so the toggle reflects any other change to it.
    //
    // PLAT-10921 — this row is left gated to end cards on purpose. The
    // creator-facing pin control lands in quick edit (PLAT-10922) and PLAT-10937
    // deletes this row when it brings that control to the panel, so generalizing
    // it here would be work with a known expiry date.
    const endCardEntry =
      videoCreator.currentVideo?.extraElementData?.[activeElement.source.id];
    const isEndCard = isEndCardMeta(endCardEntry);
    const isPinnedToEnd =
      isEndCard && readPlacementMeta(endCardEntry)?.pinned === true;

    const handleTogglePinToEnd = () => {
      const extraElementData = videoCreator.currentVideo?.extraElementData;
      if (!extraElementData) return;
      // Both the meta write and the event below key off this id — the event
      // because `composition_element_id` is what joins one graphic's whole
      // history, so reporting a blank one would silently pool unrelated
      // graphics into a single bucket rather than fail.
      const elementId = activeElement.source.id;
      if (!elementId) return;
      // The creator's own choice, so it writes `placementPinOverride` — the
      // field that wins over the authored `placementPinned` and that
      // PLAT-10922's Revert clears.
      const nextPinned = !isPinnedToEnd;
      upsertElementMetaObject(videoCreator.currentVideo, elementId, {
        placementPinOverride: nextPinned,
      });
      editorStore.editor.passExtraElementData(extraElementData);
      /*
         PLAT-10923 — the pin event. This switch is the only pin-override
         affordance that ships today, so it is the only call site; PLAT-10922
         brings the three-action control (and with it `'revert'`) to quick edit.
         `effective_pinned` equals the override we just wrote, since an override
         always wins over the authored `placementPinned`.
      */
      trackGraphicPlacementPinChanged({
        story_id: videoCreator.storyId,
        surface: 'creator-studio-settings-panel',
        composition_element_id: elementId,
        composition_id: templateId,
        action: nextPinned ? 'set_pinned' : 'set_unpinned',
        placement: composition?.placement ?? undefined,
        effective_pinned: nextPinned,
      });
      if (nextPinned) {
        // Re-pinning has to snap the card back now: the pin state changed, but
        // no timeline edit happened to carry a reconcile.
        editorStore.editor.reconcilePlacements();
      }
    };

    if (!activeElement || !templateId || !composition) return null;

    return (
      <>
        {category && subcategory && composition && (
          <div style={{ margin: '10px 0' }}>
            <Breadcrumbs
              items={[
                {
                  label: category.label || '',
                  onClick: () => {
                    setSelectedCategoryId(category.id);
                    setSelectedSubcategoryId(null);
                    videoCreator.sidebarOptions = SidebarOption.design;
                    videoCreator.setActiveElements();
                  },
                },
                {
                  // Reflect the My/Arbor group this template belongs to, not
                  // the (often duplicate) subcategory name. Derived from the
                  // template's own isGlobalPreset so it's correct regardless of
                  // transient grid chip state.
                  label: templateGroupLabel(!!composition.isGlobalPreset),
                  onClick: () => {
                    setSelectedCategoryId(category.id);
                    setSelectedSubcategoryId(subcategory.id);
                    videoCreator.sidebarOptions = SidebarOption.design;
                    videoCreator.setActiveElements();
                  },
                },
              ]}
            />
          </div>
        )}

        {/* An uploaded media card's thumbnail is the upload itself, which its
            own image/video control right below already shows (PLAT-11732). */}
        {!isUploadedMediaCardId(composition.id) && (
          <SettingsPreviewSection label={composition.label}>
            <PreviewContainer>
              {composition.thumbnail?.url ? (
                <PreviewImage
                  src={composition.thumbnail.url}
                  alt={composition.label}
                />
              ) : (
                <PreviewPlaceholder>{composition.label}</PreviewPlaceholder>
              )}
            </PreviewContainer>
          </SettingsPreviewSection>
        )}

        {composition.controls
          .filter(control => control.enabled !== false)
          .map((controlConfig: ControlConfiguration, idx: number) => (
            <div
              key={`${controlConfig.property || controlConfig.label}-${idx}`}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <CompositionControl
                control={controlConfig}
                activeElement={liveActiveElement}
                modifyProperty={modifyProperty}
                handleSpeakerControlChange={handleSpeakerControlChange}
                // Closures, not bare method references — TextProducer's
                // methods aren't auto-bound, so a raw pass loses `this`.
                convertToVh={(px, elementId) =>
                  videoCreator.textProducer.convertToVh(px, elementId)
                }
                convertToPx={(value, elementId) =>
                  videoCreator.textProducer.convertToPx(value, elementId)
                }
                compositionMeta={videoCreator.currentVideo?.extraElementData}
                aiTextContext={aiTextContext}
              />
            </div>
          ))}
        {/* PLAT-11732 — an uploaded media card has no designed motion of its
            own, so it offers the same Enter / Exit animations (fade, dip,
            cross fade…) as a plain image or video, on the whole card. */}
        {isUploadedMediaCard(composition) && (
          <GenericAnimationSettings
            activeElement={liveActiveElement}
            editorStore={editorStore}
          />
        )}
        {isEndCard && (
          <PinToEndRow>
            <Switch
              label="Pin to End of Video"
              checked={isPinnedToEnd}
              onClick={handleTogglePinToEnd}
            />
            <PinToEndInfo
              data-tooltip-id={pinToEndTooltipId}
              data-tooltip-content="When on, the card follows the end of your content. Turn it off to position it manually."
            >
              <InfoIconSmall />
            </PinToEndInfo>
            <Tooltip
              id={pinToEndTooltipId}
              place="top"
              positionStrategy="fixed"
              style={PARAGRAPH_TOOLTIP_STYLE}
            />
          </PinToEndRow>
        )}
        {/* An uploaded media card has no record to save over (PLAT-11732) */}
        {enableCompositionSaveUpdateTemplate &&
          !isUploadedMediaCardId(composition?.id) && (
            <CompositionSaveTemplate
              composition={composition}
              updatedValues={updatedValues}
              categoryId={category?.id || ''}
              subcategoryId={subcategory?.id || ''}
              activeElement={liveActiveElement}
              editorStore={editorStore}
              positionDirty={positionDirty}
              getCurrentAspectPlacement={getCurrentAspectPlacement}
              onAfterSave={() => {
                setUpdatedValues([]);
                setPositionDirty(false);
                basePlacementRef.current = livePlacementSignature;
              }}
            />
          )}
      </>
    );
  },
);

const PinToEndRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin: 10px 0;
`;

const PinToEndInfo = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
  cursor: help;
`;

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 145 / 79;
  border-radius: var(--radius-input);
  overflow: hidden;
  background: var(--surface);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  background-color: var(--background);
`;

const PreviewPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm);
  text-align: center;
  font-size: var(--text-xs);
  color: var(--accent);
  background-color: var(--background);
`;
